import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Divider,
    Link,
} from '@mui/material';

const Section = ({ number, title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {number}. {title}
        </Typography>
        {children}
    </Box>
);

const PrivacyPolicy = () => {
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Privacy Policy
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Last Updated: June 2025
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This Privacy Policy describes how <strong>Yolotech88 Pvt Ltd</strong> ("we", "us", or "our"),
                    operating under the brand name <strong>Vendora88</strong>, collects, uses, stores, and protects
                    your information when you use the Vendora88 platform, including the website{' '}
                    <Link href="https://vendora88.com" target="_blank" rel="noopener noreferrer">
                        https://vendora88.com
                    </Link>{' '}
                    and the Vendora88 mobile application (collectively, the "Service").
                    By using the Service, you agree to the collection and use of information in accordance with this policy.
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Section number={1} title="Information We Collect">
                    <Typography variant="body2" paragraph>
                        We collect the following types of information to provide and improve our Service:
                    </Typography>

                    <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                        a) Information You Provide
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0.5 }}>
                        {[
                            'Mobile phone number (used for account registration and OTP authentication)',
                            'Business name and details (for vendor accounts)',
                            'Customer names, phone numbers, and addresses (added by vendors for service delivery)',
                            'Product and pricing information',
                            'Transaction and payment records',
                            'Invoice and delivery data',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>

                    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
                        b) Information Collected Automatically
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0.5 }}>
                        {[
                            'Device information (device type, operating system)',
                            'App usage data and interaction logs',
                            'IP address and approximate location (derived from IP)',
                            'Browser type and version (for web access)',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                </Section>

                <Section number={2} title="How We Use Your Information">
                    <Typography variant="body2" paragraph>
                        We use the collected information for the following purposes:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'To provide, maintain, and improve the Vendora88 platform',
                            'To authenticate users via OTP verification',
                            'To enable vendors to manage products, subscriptions, deliveries, invoices, and payments',
                            'To send service-related notifications (OTPs, delivery reminders, invoice alerts, payment confirmations)',
                            'To generate invoices and billing statements',
                            'To monitor platform usage and improve user experience',
                            'To detect and prevent fraud or unauthorized access',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                </Section>

                <Section number={3} title="Data Sharing and Disclosure">
                    <Typography variant="body2" paragraph>
                        We do not sell, trade, or rent your personal information to third parties. We may share
                        information only in the following circumstances:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'With service providers who assist in operating the platform (e.g., SMS gateway providers for OTP delivery, cloud hosting providers)',
                            'When required by law, regulation, or legal process',
                            'To protect the rights, safety, or property of Yolotech88 Pvt Ltd, our users, or the public',
                            'With your explicit consent',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Vendors are responsible for ensuring they have obtained appropriate consent from their
                        customers before adding customer data to the platform.
                    </Typography>
                </Section>

                <Section number={4} title="Data Storage and Security">
                    <Typography variant="body2" paragraph>
                        Your data is stored on secure cloud servers. We implement appropriate technical and
                        organizational measures to protect your information, including:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Encrypted data transmission (HTTPS/TLS)',
                            'Secure token-based authentication',
                            'Regular security updates and monitoring',
                            'Access controls to limit data access to authorized personnel',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        While we strive to protect your information, no method of electronic transmission or
                        storage is 100% secure. We cannot guarantee absolute security.
                    </Typography>
                </Section>

                <Section number={5} title="Data Retention">
                    <Typography variant="body2" paragraph>
                        We retain your personal information for as long as your account is active or as needed
                        to provide you with our services. We may also retain data as necessary to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Comply with legal obligations',
                            'Resolve disputes',
                            'Enforce our agreements',
                            'Maintain business records as required by applicable laws',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        When data is no longer required, it will be securely deleted or anonymized.
                    </Typography>
                </Section>

                <Section number={6} title="Your Rights">
                    <Typography variant="body2" paragraph>
                        You have the following rights regarding your personal data:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Access: Request a copy of the data we hold about you',
                            'Correction: Request correction of inaccurate or incomplete data',
                            'Deletion: Request deletion of your account and associated data',
                            'Portability: Request your data in a commonly used format',
                            'Objection: Object to the processing of your data for specific purposes',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        To exercise any of these rights, please contact us using the details provided below.
                    </Typography>
                </Section>

                <Section number={7} title="Third-Party Services">
                    <Typography variant="body2" paragraph>
                        Our Service may use third-party services for functionality such as SMS delivery,
                        cloud hosting, and analytics. These third-party providers have their own privacy
                        policies governing the use of your information.
                    </Typography>
                    <Typography variant="body2">
                        We are not responsible for the privacy practices of third-party services.
                    </Typography>
                </Section>

                <Section number={8} title="Children's Privacy">
                    <Typography variant="body2">
                        Our Service is not intended for use by individuals under the age of 18. We do not
                        knowingly collect personal information from children. If we become aware that we have
                        collected personal data from a child without parental consent, we will take steps to
                        remove that information.
                    </Typography>
                </Section>

                <Section number={9} title="Changes to This Privacy Policy">
                    <Typography variant="body2">
                        We may update this Privacy Policy from time to time. Any changes will be posted on this
                        page with an updated "Last Updated" date. We encourage you to review this policy
                        periodically. Continued use of the Service after changes constitutes acceptance of the
                        updated policy.
                    </Typography>
                </Section>

                <Section number={10} title="Contact Us">
                    <Typography variant="body2" paragraph>
                        If you have any questions, concerns, or requests regarding this Privacy Policy or your
                        personal data, please contact us:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                        <Typography variant="body2"><strong>Company:</strong> Yolotech88 Pvt Ltd</Typography>
                        <Typography variant="body2"><strong>Platform:</strong> Vendora88</Typography>
                        <Typography variant="body2">
                            <strong>Website:</strong>{' '}
                            <Link href="https://vendora88.com" target="_blank" rel="noopener noreferrer">
                                https://vendora88.com
                            </Link>
                        </Typography>
                        <Typography variant="body2">
                            <strong>Email:</strong>{' '}
                            <Link href="mailto:vendora88@yolotech88.com">vendora88@yolotech88.com</Link>
                        </Typography>
                    </Box>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Typography variant="body2" color="text.secondary" textAlign="center">
                    © {new Date().getFullYear()} Yolotech88 Pvt Ltd. All rights reserved.
                </Typography>
            </Paper>
        </Container>
    );
};

export default PrivacyPolicy;
